// Recipe registry and cooking helpers for the kitchen/crafting loop.
// Dishes are cooked at the house stove (or later a bakery) and consumed for
// temporary buffs. Recipes can be known from the start or discovered by cooking
// the right ingredients.

import { getState, save, spendCrops } from './save.js';
import { spendIngredient } from './ingredients.js';
import { addDish } from './dishes.js';

export const RECIPES = {
  'toast-jam': {
    name: 'Toast & Jam',
    icon: '🍞',
    desc: 'A classic. Sweet and sturdy.',
    ingredients: { 'crop:wheat': 1, 'berry': 1 },
    buff: { str: 1 },
    duration: 180,      // seconds
    difficulty: 0.25
  },
  'crust-coffee': {
    name: 'Crust Coffee',
    icon: '☕',
    desc: 'Roasted crust grounds. Wakes you right up.',
    ingredients: { 'crop:wheat': 1, 'herb': 1 },
    buff: { agi: 1 },
    duration: 180,
    difficulty: 0.30
  },
  'hearty-loaf': {
    name: 'Hearty Loaf',
    icon: '🥖',
    desc: 'Mushrooms make everything heartier.',
    ingredients: { 'crop:wheat': 2, 'mushroom': 1 },
    buff: { str: 1, agi: 1 },
    duration: 300,
    difficulty: 0.35
  },
  'ghostly-gelato': {
    name: 'Ghostly Gelato',
    icon: '🍨',
    desc: 'Cold, creamy, and faintly translucent.',
    ingredients: { 'berry': 1, 'honey': 1 },
    buff: { int: 1, zen: 1 },
    duration: 300,
    difficulty: 0.40
  },
  'bone-broth': {
    name: 'Bone Broth',
    icon: '🍲',
    desc: 'Boiled from crypt leftovers. Packs a punch.',
    ingredients: { 'bone-meal': 1, 'mushroom': 1 },
    buff: { str: 2 },
    duration: 300,
    difficulty: 0.45
  },
  'golden-croissant': {
    name: 'Golden Croissant',
    icon: '🥐',
    desc: 'Layers of butter and sunshine.',
    ingredients: { 'crop:wheat': 1, 'honey': 1 },
    buff: { int: 1 },
    duration: 240,
    difficulty: 0.35
  },
  'royal-tart': {
    name: 'Royal Tart',
    icon: '🥧',
    desc: 'A dish fit for the Baker King.',
    ingredients: { 'crop:wheat': 1, 'berry': 1, 'honey': 1 },
    buff: { str: 1, agi: 1, int: 1, zen: 1 },
    duration: 300,
    difficulty: 0.55
  },
  'big-t-burger': {
    name: "Big T's Burger",
    icon: '🍔',
    desc: 'A smoky grilled patty on a toasted bun. Big T approved.',
    ingredients: { 'patty': 1, 'crop:wheat': 1, 'bbq-sauce': 1 },
    buff: { str: 2, agi: 1 },
    duration: 300,
    difficulty: 0.45
  },
  'charred-chop': {
    name: 'Charred Chop',
    icon: '🥩',
    desc: 'Grilled to the edge of burnt. Crunchy.',
    ingredients: { 'patty': 1, 'herb': 1 },
    buff: { str: 1 },
    duration: 240,
    difficulty: 0.35
  },
  // ---- moon recipes (learned by reaching the moon; cooked with lunar goods) ----
  'moon-cheese-toast': {
    name: 'Moon Cheese Toast',
    icon: '🌙',
    desc: 'Glowing cheese from the lunar dairy on toasted space bun.',
    ingredients: { 'crop:wheat': 1, 'moon_cheese': 1 },
    buff: { int: 1, zen: 1 },
    duration: 300,
    difficulty: 0.4,
    moon: true
  },
  'crater-souffle': {
    name: 'Crater Soufflé',
    icon: '🥮',
    desc: 'A fluffy dish that floats in low gravity.',
    ingredients: { 'moon_egg': 1, 'moon_cheese': 1, 'stardust': 1 },
    buff: { agi: 2 },
    duration: 360,
    difficulty: 0.55,
    moon: true
  },
  'astro-burger': {
    name: 'Astro Burger',
    icon: '🍔',
    desc: 'A space-age burger in a vacuum-sealed bun.',
    ingredients: { 'space_bun': 1, 'moon_cheese': 1, 'nebula_lettuce': 1 },
    buff: { str: 2, agi: 1 },
    duration: 360,
    difficulty: 0.6,
    moon: true
  },
  'starlight-pudding': {
    name: 'Starlight Pudding',
    icon: '🍮',
    desc: 'Dessert that shimmers like the Milky Way.',
    ingredients: { 'moon_milk': 1, 'stardust': 1, 'comet_sugar': 1 },
    buff: { int: 2, zen: 2 },
    duration: 420,
    difficulty: 0.65,
    moon: true
  }
};

export function getKnownRecipes(){ return getState().recipes || []; }

export function knowRecipe(id){
  const r = getState().recipes;
  return r.includes(id);
}

export function learnRecipe(id){
  const r = getState().recipes;
  if (r.includes(id)) return false;
  r.push(id);
  save();
  return true;
}

// Resolve a recipe ingredient key to a spend helper.
// Keys: 'crop:<id>' or plain ingredient id.
function spendResource(key, amount){
  if (key.startsWith('crop:')){
    return spendCrops(key.slice(5), amount);
  }
  return spendIngredient(key, amount);
}

function countResource(key, inventory){
  if (key.startsWith('crop:')){
    return inventory.crops[key.slice(5)] || 0;
  }
  return inventory.ingredients?.[key] || 0;
}

export function canCook(id){
  const recipe = RECIPES[id];
  if (!recipe) return false;
  const inv = getState().inventory;
  for (const [key, amount] of Object.entries(recipe.ingredients)){
    if (countResource(key, inv) < amount) return false;
  }
  return true;
}

// Attempt to cook a recipe. `quality` is 0..1 from the minigame.
// Returns { ok, dishId, qualityLabel, learned } or { ok:false, reason }.
export function cook(id, quality = 0.5){
  const recipe = RECIPES[id];
  if (!recipe) return { ok: false, reason: 'unknown recipe' };
  if (!canCook(id)) return { ok: false, reason: 'missing ingredients' };

  for (const [key, amount] of Object.entries(recipe.ingredients)){
    if (!spendResource(key, amount)) return { ok: false, reason: 'missing ingredients' };
  }

  const label = quality >= 0.85 ? 'perfect' : quality >= 0.55 ? 'good' : 'burnt';
  if (label === 'burnt'){
    return { ok: true, dishId: null, quality, qualityLabel: 'burnt', learned: false };
  }

  const learned = learnRecipe(id);
  addDish(id, 1);
  return { ok: true, dishId: id, quality, qualityLabel: label, learned };
}

// Check a freeform set of ingredient ids against recipes for experimentation.
export function matchExperiment(ingredients){
  const sorted = [...ingredients].sort().join(',');
  for (const id in RECIPES){
    const needed = Object.keys(RECIPES[id].ingredients).sort().join(',');
    if (needed === sorted) return id;
  }
  return null;
}
